import { ReactElement, createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PieceTheme, PieceSymbol, DEFAULT_PIECES, UNICODE_PIECES } from '../types/pieces';
import type { PieceProviderProps } from '../types';
import { PIECE_SVGS } from '../utils/pieceConstants';
import { createEmptyPieceRecord } from '../utils/pieceUtils';

interface PieceContextType {
  currentTheme: string;
  themes: Record<string, PieceTheme>;
  loadTheme: (name: string) => Promise<void>;
  getPieceSvg: (piece: PieceSymbol) => string | null;
  setCustomPiece: (piece: PieceSymbol, url: string) => void;
  resetTheme: () => void;
  preloadTheme: (name: string) => Promise<void>;
  getThemeNames: () => string[];
  isThemeLoaded: (name: string) => boolean;
}

const PieceContext = createContext<PieceContextType>({
  currentTheme: 'default',
  themes: {},
  loadTheme: async () => {},
  getPieceSvg: () => null,
  setCustomPiece: () => {},
  resetTheme: () => {},
  preloadTheme: async () => {},
  getThemeNames: () => [],
  isThemeLoaded: () => false
});

export const usePieces = () => {
  const context = useContext(PieceContext);
  if (!context) {
    throw new Error('usePieces must be used within a PieceProvider');
  }
  return context;
};

export function PieceProvider({ 
  children, 
  defaultTheme = 'default',
  customPieces = createEmptyPieceRecord(),
  preloadThemes = false
}: PieceProviderProps): ReactElement {
  const [themes, setThemes] = useState<Record<string, PieceTheme>>({
    default: {
      name: 'default',
      pieces: PIECE_SVGS
    }
  });
  const [currentTheme, setCurrentTheme] = useState(defaultTheme);
  const [loadingThemes, setLoadingThemes] = useState<Set<string>>(new Set());
  const [loadedThemes, setLoadedThemes] = useState<Set<string>>(new Set(['default']));

  const validatePieceSymbol = (piece: PieceSymbol): boolean => {
    return Object.keys(PIECE_SVGS).includes(piece);
  };

  const loadTheme = async (name: string) => {
    if (themes[name] || loadingThemes.has(name)) return;
    
    // Skip loading default theme since it's already initialized
    if (name === 'default') return;

    try {
      setLoadingThemes(prev => new Set([...prev, name]));
      
      let themePieces = createEmptyPieceRecord();
      
      if (name === 'unicode') {
        themePieces = UNICODE_PIECES;
      } else {
        // Load custom theme pieces
        // This is where you would implement custom theme loading logic
        throw new Error(`Theme '${name}' not found`);
      }

      const theme: PieceTheme = {
        name,
        pieces: themePieces
      };

      setThemes(prev => ({
        ...prev,
        [name]: theme
      }));
      setLoadedThemes(prev => new Set([...prev, name]));
      setCurrentTheme(name);
    } catch (error) {
      console.error(`Failed to load theme: ${name}`, error);
      throw error;
    } finally {
      setLoadingThemes(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  const setCustomPiece = useCallback((piece: PieceSymbol, url: string) => {
    if (!validatePieceSymbol(piece)) {
      throw new Error(`Invalid piece symbol: ${piece}`);
    }

    setThemes(prev => ({
      ...prev,
      [currentTheme]: {
        ...prev[currentTheme],
        pieces: {
          ...prev[currentTheme].pieces,
          [piece]: url
        }
      }
    }));
  }, [currentTheme]);

  const resetTheme = useCallback(() => {
    if (currentTheme === 'default') {
      setThemes(prev => ({
        ...prev,
        default: {
          name: 'default',
          pieces: PIECE_SVGS
        }
      }));
    }
  }, [currentTheme]);

  const preloadTheme = async (name: string) => {
    if (loadedThemes.has(name)) return;
    await loadTheme(name);
  };

  const getPieceSvg = useCallback((piece: PieceSymbol): string | null => {
    if (!validatePieceSymbol(piece)) {
      console.error(`Invalid piece symbol: ${piece}`);
      return null;
    }

    if (currentTheme === 'default') {
      return PIECE_SVGS[piece] || null;
    }

    const theme = themes[currentTheme];
    if (!theme) {
      return PIECE_SVGS[piece] || null;
    }

    return theme.pieces[piece] || PIECE_SVGS[piece] || null;
  }, [themes, currentTheme]);

  const getThemeNames = useCallback(() => {
    return Object.keys(themes);
  }, [themes]);

  const isThemeLoaded = useCallback((name: string) => {
    return loadedThemes.has(name);
  }, [loadedThemes]);

  // Load default theme and custom pieces on mount
  useEffect(() => {
    const init = async () => {
      try {
        await loadTheme('default');
        
        // Apply custom pieces if provided
        Object.entries(customPieces).forEach(([piece, url]) => {
          setCustomPiece(piece as PieceSymbol, url);
        });

        // Load default theme if different from 'default'
        if (defaultTheme !== 'default') {
          await loadTheme(defaultTheme);
        }

        // Preload additional themes if enabled
        if (preloadThemes) {
          await Promise.all([
            loadTheme('unicode')
            // Add more theme preloading here
          ]);
        }
      } catch (error) {
        console.error('Error initializing PieceProvider:', error);
      }
    };

    init();
  }, [defaultTheme, customPieces, preloadThemes, setCustomPiece]);

  return (
    <PieceContext.Provider value={{
      currentTheme,
      themes,
      loadTheme,
      getPieceSvg,
      setCustomPiece,
      resetTheme,
      preloadTheme,
      getThemeNames,
      isThemeLoaded
    }}>
      {children}
    </PieceContext.Provider>
  );
} 